import React, { useState } from 'react';
import { TrendingUp, Eye, Users, Calendar } from 'lucide-react';
import { authAxios } from '@/lib/api';
import { useApiData } from '@/hooks/useApiData';
import StatCard from './analytics/StatCard';
import DailyViewsChart from './analytics/DailyViewsChart';
import DeviceBreakdown from './analytics/DeviceBreakdown';
import { TopPages, TopProducts, TopBlogs, TopReferrers } from './analytics/TopLists';

const PERIODS = [7, 30, 90];

function PeriodSelector({ days, onChange, totalAll }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Calendar size={14} className="text-earth/40" />
        <span className="text-xs text-earth/50 font-sans">Period:</span>
        {PERIODS.map(d => (
          <button
            key={d}
            onClick={() => onChange(d)}
            className={`text-xs font-sans px-3 py-1.5 rounded-md transition-colors ${days === d ? 'bg-saffron text-white font-semibold' : 'bg-white text-earth/50 border border-earth/15 hover:border-saffron/30'}`}
            data-testid={`period-${d}d`}
          >
            {d}d
          </button>
        ))}
      </div>
      <p className="text-[10px] text-earth/30 font-sans">{totalAll} total page views recorded</p>
    </div>
  );
}

function SummaryStats({ data, days }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <StatCard icon={Eye} label="Views (Period)" value={data.total_period} sub={`Last ${days} days`} color="#D4870A" />
      <StatCard icon={TrendingUp} label="Today" value={data.total_today} color="#6B8E23" />
      <StatCard icon={Eye} label="This Week" value={data.total_week} color="#E8A020" />
      <StatCard icon={Users} label="Unique Visitors" value={data.unique_visitors} sub={`Last ${days} days`} color="#C0392B" />
    </div>
  );
}

export default function AnalyticsDashboard() {
  const [days, setDays] = useState(30);
  const { data, loading } = useApiData(`/api/analytics/dashboard?days=${days}`, null, authAxios);

  if (loading) {
    return (
      <div className="text-center py-16 text-earth/40 font-sans" data-testid="analytics-loading">
        <TrendingUp size={32} className="mx-auto mb-3 text-earth/20 animate-pulse" />
        Loading analytics...
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-16 text-earth/40 font-sans" data-testid="analytics-error">Failed to load analytics data</div>;
  }

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      <PeriodSelector days={days} onChange={setDays} totalAll={data.total_all} />
      <SummaryStats data={data} days={days} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <DailyViewsChart dailyViews={data.daily_views} />
        <DeviceBreakdown devices={data.devices} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <TopPages pages={data.top_pages} />
        <TopProducts products={data.top_products} />
        <div className="space-y-6">
          <TopBlogs blogs={data.top_blogs} />
          <TopReferrers referrers={data.referrers} />
        </div>
      </div>
    </div>
  );
}
